<template>
  <Context
    ref="context"
    class="formula-input-explorer-context"
    max-height-if-outside-viewport
    overflow-scroll
    data-formula-input-context
    :hide-on-click-outside="false"
  >
    <NodeExplorer
      :node-selected="nodeSelected"
      :mode="mode"
      :nodes-hierarchy="nodesHierarchy"
      :allow-node-selection="allowNodeSelection"
      :loading="loading"
      @node-selected="$emit('node-selected', $event)"
      @node-unselected="$emit('node-unselected')"
    />
    <div
      v-if="advancedModeEnabled"
      class="formula-input-explorer-context__footer"
    >
      <ButtonText
        type="primary"
        icon="iconoir-input-field"
        size="small"
        @click="toggleMode"
        >{{
          isAdvancedMode
            ? $t('formulaInputExplorerContext.useSimpleInput')
            : $t('formulaInputExplorerContext.useAdvancedInput')
        }}</ButtonText
      >
    </div>

    <Modal
      ref="advancedModeModal"
      class="formula-input-explorer-context__advanced-mode-modal"
    >
      <h2 class="box__title">
        {{
          isAdvancedMode
            ? $t('formulaInputExplorerContext.useSimpleInputModalTitle')
            : $t('formulaInputExplorerContext.useAdvancedInputModalTitle')
        }}
      </h2>
      <p>{{ $t('formulaInputExplorerContext.modalMessage') }}</p>

      <div class="actions actions--right actions--gap margin-bottom-0">
        <Button type="secondary" size="large" @click="cancelModeChange">
          {{ $t('action.cancel') }}
        </Button>
        <Button type="danger" size="large" @click="confirmModeChange">
          {{
            isAdvancedMode
              ? $t('formulaInputExplorerContext.useSimpleInput')
              : $t('formulaInputExplorerContext.useAdvancedInput')
          }}
        </Button>
      </div>
    </Modal>
  </Context>
</template>

<script>
import context from '@baserow/modules/core/mixins/context'
import NodeExplorer from '@baserow/modules/core/components/nodeExplorer/NodeExplorer'
import { BASEROW_FORMULA_MODES } from '@baserow/modules/core/formula/constants'

export default {
  name: 'FormulaInputExplorerContext',
  components: {
    NodeExplorer,
  },
  mixins: [context],
  props: {
    nodeSelected: {
      type: String,
      required: false,
      default: null,
    },
    nodesHierarchy: {
      type: Array,
      required: false,
      default: () => [],
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    mode: {
      type: String,
      required: false,
      default: 'advanced',
      validator: (value) => {
        return BASEROW_FORMULA_MODES.includes(value)
      },
    },
    /**
     * Whether the formula input has a formula value set or not.
     * Used to determine if we need to show a confirmation prompt
     * or not when the mode changes from advanced to simple.
     */
    hasValue: {
      type: Boolean,
      required: false,
      default: false,
    },
    allowNodeSelection: {
      type: String,
      required: false,
      default: 'none',
      validator: (value) => ['none', 'all', 'array', 'object'].includes(value),
    },
    /**
     * An array of Baserow formula modes which the parent formula input
     * component allows to be used. By default, in `FormulaInputField`,
     * we will allow all modes.
     */
    enabledModes: {
      type: Array,
      required: true,
    },
  },
  emits: ['mode-changed', 'node-selected', 'node-unselected'],
  data() {
    return {
      searchQuery: '',
      tooltip: {
        functionData: null,
      },
      tooltipTimer: null,
      tabs: [],
      isModalVisible: false,
    }
  },
  computed: {
    advancedModeEnabled() {
      return this.enabledModes.includes('advanced')
    },
    isAdvancedMode() {
      return this.mode === 'advanced'
    },
  },
  watch: {
    mode() {
      this.$nextTick(() => {
        this.activeTabIndex = 0
      })
    },
    activeTabIndex() {
      this.searchQuery = ''
      this.hideTooltip()
    },
  },
  created() {
    this.tabs = this.nodes
  },
  methods: {
    show(
      targetElement,
      verticalPosition = 'bottom',
      horizontalPosition = 'left',
      verticalOffset = 0,
      horizontalOffset = 0
    ) {
      return this.$refs.context.show(
        targetElement,
        verticalPosition,
        horizontalPosition,
        verticalOffset,
        horizontalOffset
      )
    },
    hide() {
      this.$refs.context.hide()
      this.hideTooltip()
    },
    getTabTitle(tabName) {
      const titleMap = {
        Functions: this.$t('formulaInputExplorerContext.functions'),
        Operators: this.$t('formulaInputExplorerContext.operators'),
      }
      return titleMap[tabName] || tabName
    },

    getFilteredItems(items, tabName) {
      if (!items || !this.searchQuery) {
        return items || []
      }

      return items.filter(
        (item) =>
          item.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          (item.description &&
            item.description
              .toLowerCase()
              .includes(this.searchQuery.toLowerCase()))
      )
    },
    toggleMode() {
      if (this.mode === 'advanced') {
        if (this.hasValue) {
          // If we have a value then we want the user to confirm
          // they're happy for the formula to be reset.
          this.showAdvancedModeModal()
        } else {
          // If we have no value then we can safely switch modes.
          this.$emit('mode-changed', 'simple')
        }
      } else {
        this.$emit('mode-changed', 'advanced')
      }
    },
    onFunctionHover(item, tabName, event) {
      if (tabName !== 'Functions') {
        return
      }

      if (this.tooltipTimer) {
        clearTimeout(this.tooltipTimer)
      }

      this.tooltip.functionData = {
        name: item.name,
        description: item.description,
        example: item.example,
        icon: item.icon,
      }

      this.tooltipTimer = setTimeout(() => {
        if (this.$refs.functionHelpTooltip) {
          this.$refs.functionHelpTooltip.show(
            event.target,
            'bottom',
            'right',
            5,
            10
          )
        }
      }, 300)
    },
    onFunctionLeave() {
      if (this.tooltipTimer) {
        clearTimeout(this.tooltipTimer)
        this.tooltipTimer = null
      }

      this.hideTooltip()
    },
    hideTooltip() {
      if (this.$refs.functionHelpTooltip) {
        this.$refs.functionHelpTooltip.hide()
      }
      this.tooltip.functionData = null
    },
    showAdvancedModeModal() {
      this.$refs.advancedModeModal.show()
    },
    confirmModeChange() {
      this.$refs.advancedModeModal.hide()
      this.$emit(
        'mode-changed',
        this.mode === 'advanced' ? 'simple' : 'advanced'
      )
    },
    cancelModeChange() {
      this.$refs.advancedModeModal.hide()
    },
  },
}
</script>
